import {useState, useRef, useEffect} from 'react'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {
    planeTakeOff,
    plane2d,
    closeLight,
    boldCar,
    infoIcon,
    arrRight,
    selectedIcon,
    planeTakeoffBold,
    clockIconBold,
    stopWatchBold,
    cardBold,
} from '@img/index.js'
import {flightService} from '@/services/flightService'
import {isNearAirport} from '@/utils/airports'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {formatBufferApprox} from '@/utils/policyFormat'
import {airportContext, isFlightMismatch, flightMismatchMessage, isFlightCancelled, resolveMovementTime, getRecommendedTime, getSmartTimeError, formatSmartTime, formatSmartDateLabel} from '@/utils/smartTime'
import FlightMismatchWarning from '@/customComponents/sharedComponents/smartTime/FlightMismatchWarning.jsx'
import FlightCancelledWarning from '@/customComponents/sharedComponents/smartTime/FlightCancelledWarning.jsx'
import FlightUnavailableWarning from '@/customComponents/sharedComponents/smartTime/FlightUnavailableWarning.jsx'
import TimeErrorWarning from '@/customComponents/sharedComponents/smartTime/TimeErrorWarning.jsx'
import ToAirportRecommendedCard from '@/customComponents/sharedComponents/smartTime/ToAirportRecommendedCard.jsx'
import {waitingItemImage} from '@/customComponents/sharedComponents/smartTime/waitingIcons'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'

export default function DateTimeSection({
                                            destinations,
                                            routeData,
                                            rideDate,
                                            onSelectTime,
                                            onClose,
                                            hideHeader = false,
                                            hideFlightInput = false,
                                            hideManualOption = false,
                                            hideTimeSuggestion = false,
                                            hintContent = null,
                                            flightNumber,
                                            setFlightNumber,
                                            flightDetails,
                                            setFlightDetails,
                                            flightLoading,
                                            setFlightLoading,
                                            flightError,
                                            setFlightError,
                                            // Opens the parent submit gate when the user clicks "Proceed anyway" on the
                                            // provider-unavailable card (see hasFlightNumber in useEditTrip). NOT called
                                            // automatically — only on explicit acknowledgement.
                                            setFlightUnavailable = () => {},
                                            timeApplied,
                                            setTimeApplied,
                                            dateTimeSectionRef,
                                            flightPolicy = {canEdit: true, blockedReason: null},
                                            onDropStateChange = null,
                                        }) {
    const autoLookedUp = useRef(false)
    const userTyped = useRef(false)

    // fromAirport pickup sections behave as a radio group — recommended vs custom
    const [selectedSection, setSelectedSection] = useState('recommended')
    const [toAirportExpanded, setToAirportExpanded] = useState(false)
    // Flight/airport mismatch soft-validation: warn + gate the recommended pickup until
    // the user clicks "Proceed anyway".
    const [acknowledged, setAcknowledged] = useState(false)
    const [proceeding, setProceeding] = useState(false)
    // Last lookup couldn't reach the flight provider (network/HTTP failure, not a clean
    // "no such flight"). Shows the FlightUnavailableWarning card; local to this section.
    const [providerUnavailable, setProviderUnavailable] = useState(false)
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 575)
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 575)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])
    const isRecommendedActive = selectedSection === 'recommended'
    const isCustomActive = selectedSection === 'custom'

    const origin = destinations?.origin
    const dest = destinations?.destination
    const {isFromAirport, selectedAirportCode, airportBuffer, waitingItems, arriveEarlyMinutes} =
        airportContext(origin, dest, isNearAirport, flightDetails)
    const checkMismatch = (fd) => isFlightMismatch(fd, {isFromAirport, selectedAirportCode})
    const flightMismatch = checkMismatch(flightDetails)
    const flightMismatchMsg = flightMismatchMessage(flightDetails, {isFromAirport, selectedAirportCode})
    // A cancelled flight gates the recommendation the same way a mismatch does.
    const flightCancelled = isFlightCancelled(flightDetails)
    const flightBlocked = flightMismatch || flightCancelled

    // Effective (revised ▸ predicted ▸ scheduled) times, so a delayed flight shows the
    // new time with the original struck through. Time/date changes tracked separately.
    const depTime = resolveMovementTime(flightDetails?.departure)
    const arrTime = resolveMovementTime(flightDetails?.arrival)

    // Tell the opener whether closing now should drop the flight: an airport mismatch the user
    // never acknowledged ("Proceed anyway" not clicked) means the flight was never confirmed.
    useEffect(() => {
        onDropStateChange?.(flightMismatch && !acknowledged)
    }, [onDropStateChange, flightMismatch, acknowledged])

    // Auto-fetch flight when pre-filled (waits until rideDate is populated so we always
    // hit the date-scoped endpoint, matching the manual lookup flow). Skipped if the
    // current flightNumber came from user typing — that path uses the Add button.
    useEffect(() => {
        if (!flightNumber || autoLookedUp.current || flightDetails || !rideDate || userTyped.current) return
        autoLookedUp.current = true
        ;(async () => {
            setFlightLoading(true)
            const d = new Date(rideDate)
            const dateParam = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            const result = await flightService.getFlightDetails(flightNumber, dateParam, isFromAirport ? 'Arrival' : 'Departure')
            setFlightLoading(false)
            if (result.success && result.data.length > 0) {
                const fd = result.data[0]
                // Auto-acknowledge only an unblocked flight — a mismatch OR a cancellation
                // must hold the gate until the user clicks "Proceed anyway".
                setAcknowledged(!checkMismatch(fd) && !isFlightCancelled(fd))
                setFlightDetails(fd)
                setProviderUnavailable(false)
                setFlightUnavailable(false)   // provider recovered → close the outage escape hatch
            } else if (result.unavailable) {
                // Provider unreachable — show the "Fail to get the details from server"
                // card with "Proceed anyway" instead of hard-failing (the flight is still
                // saved on the booking). Don't open the gate until the user acknowledges.
                setProviderUnavailable(true)
            } else {
                // Provider answered, but there's genuinely no such flight for this date.
                setFlightError(`Your flight number ${flightNumber} is saved with this booking, but we couldn't fetch current flight details (it may be postponed, cancelled, or temporarily unavailable).`)
            }
        })()
    }, [rideDate, flightNumber])

    const handleFlightLookup = async (e) => {
        e.preventDefault()
        if (!flightNumber.trim()) return

        setFlightLoading(true)
        setFlightDetails(null)
        setFlightError('')

        let dateParam
        if (rideDate) {
            const d = new Date(rideDate)
            dateParam = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        }
        const result = await flightService.getFlightDetails(flightNumber, dateParam, isFromAirport ? 'Arrival' : 'Departure')
        setFlightLoading(false)

        if (!result.success) {
            if (result.unavailable) {
                // Provider unreachable → warning card + "Proceed anyway", not a dead error.
                setProviderUnavailable(true)
            } else {
                setFlightError(result.error || 'Failed to fetch flight details');
            }
            return
        }
        if (result.data.length > 0) {
            const fd = result.data[0]
            setProviderUnavailable(false)
            setFlightUnavailable(false)
            setAcknowledged(!checkMismatch(fd) && !isFlightCancelled(fd))
            setProceeding(false)
            setFlightDetails(fd);
            setTimeApplied(false)
        }
    }

    // "Proceed anyway" on the provider-unavailable card: acknowledge locally (so the card
    // drops its button) AND open the parent submit gate — the user has explicitly chosen
    // to continue with the flight already saved on the booking.
    const handleProceedUnavailable = () => {
        setProceeding(true)
        setTimeout(() => {
            setAcknowledged(true)
            setProceeding(false)
            setFlightUnavailable(true)   // opens the submit gate (hasFlightNumber)
            // Nothing more to do in this modal (no details ⇒ no recommendation), and the
            // flight is acknowledged — close back to the edit page so the gate is visible.
            onClose?.()
        }, 500)
    }

    // Mismatch acknowledged → reveal the recommended pickup (brief spinner on the button).
    const handleProceedAnyway = () => {
        setProceeding(true)
        setTimeout(() => { setAcknowledged(true); setProceeding(false) }, 500)
    }

    const formatTime = formatSmartTime
    const formatDateLabel = formatSmartDateLabel

    const calc = getRecommendedTime({flightDetails, isFromAirport, airportBuffer, travelMinutes: routeData?.duration_minutes || 0})
    const confirmDateTimeStr = calc ? `${formatTime(calc.recommendedTime)}, ${formatDateLabel(calc.recommendedTime)}` : ''
    const timeError = getSmartTimeError(calc?.recommendedTime)

    const handleSelectTime = () => {
        if (!calc) return
        onSelectTime?.(calc.recommendedTime)
        setTimeApplied(true)
    }

    // Apply the recommended time and close the smart modal
    const confirmPickupTime = () => {
        handleSelectTime()
        onClose?.()
    }

    return (
        <>
            {!hideHeader && <ModalHeader title="Smart Time Select" onClose={onClose}/>}

            {!hideFlightInput && <h6 className="font-bold mb-3!">Enter Flight Number</h6>}
            {hintContent}

            <div className="border border-(--bg-dark-grey) rounded-2xl p-5 max-md:p-3">
                {!hideFlightInput && (
                <div className="rounded-2xl p-5 shadow-[0_4px_24px_#201e2014]">
                    <form className="flex items-center gap-2 relative" onSubmit={(e) => {
                        e.preventDefault()
                        if (flightDetails) {
                            setFlightDetails(null);
                            setFlightNumber('');
                            setFlightError('');
                            setAcknowledged(false);
                            setProceeding(false);
                            return
                        }
                        handleFlightLookup(e)
                    }}>
                        <img alt="flight number" src={planeTakeOff}
                             className="absolute top-1/2 -mt-2.5 left-4 opacity-70 w-6"/>
                        <input
                            type="text" value={flightNumber} placeholder="E.g. AC4567"
                            disabled={!flightPolicy.canEdit}
                            onChange={(e) => {
                                userTyped.current = true
                                if (flightDetails) {
                                    setFlightDetails(null);
                                    setFlightError('')
                                    setAcknowledged(false)
                                    setProceeding(false)
                                }
                                // Editing the number invalidates a prior provider-unavailable
                                // acknowledgement — start the next lookup clean.
                                if (providerUnavailable) {
                                    setProviderUnavailable(false)
                                    setFlightUnavailable(false)
                                    setAcknowledged(false)
                                }
                                setFlightNumber(e.target.value)
                            }}
                            className="flex-1 min-w-0 w-full border border-(--border-color) rounded-full px-12 py-3.5 outline-none text-base disabled:opacity-50 disabled:cursor-not-allowed"
                            required="true"
                        />
                        <div className="absolute right-1.5 group">
                            <button type="submit" disabled={flightLoading || !flightPolicy.canEdit}
                                    className="w-[2.625rem] h-[2.625rem] shrink-0 bg-(--accent-green-light) rounded-full border-0 cursor-pointer text-(--text-primary) flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                                {flightDetails ? <img src={closeLight} alt="Remove" className="w-4"/> : 'Add'}
                            </button>
                            {!flightPolicy.canEdit && flightPolicy.blockedReason && (
                                <div className="hidden group-hover:block absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                                    <p className="mb-0!">{flightPolicy.blockedReason}</p>
                                </div>
                            )}
                        </div>
                    </form>
                </div>
                )}

                {flightError && <p className="text-red-500 text-sm mb-3">{flightError}</p>}

                {flightLoading && <div className="text-center py-4"><Loader/></div>}

                {/* Flight Details Card */}
                {flightDetails && (
                    <div className="pt-5">
                        <div className="text-sm mb-3">
                            {flightDetails.airline?.name || flightDetails.number}
                            {flightDetails.aircraft?.model &&
                                <span className="font-bold text-lg text-(--text-primary-light)"> – {flightDetails.aircraft.model}</span>}
                        </div>
                        <div className="relative flex items-center justify-between mb-8 gap-3 flex-wrap">
                            <span className="pointer-events-none absolute left-1/3 right-1/3 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-(--bg-dark-grey)"></span>
                            <div className="text-sm min-w-0 flex-1">
                                <span className="block">
                                    {flightDetails.departure?.airport?.municipalityName || '—'} {flightDetails.departure?.airport?.iata ? `(${flightDetails.departure.airport.iata})` : ''}
                                </span>
                                <span className="block text-5xl font-bold">
                                    {depTime.timeChanged ? (
                                        <>
                                            <span className="mr-2.5">{flightService.formatFlightTime(depTime.effective)}</span>
                                            <span className="text-lg line-through">{flightService.formatFlightTime(depTime.scheduled)}</span>
                                        </>
                                    ) : (
                                        depTime.scheduled ? flightService.formatFlightTime(depTime.scheduled) : '—'
                                    )}
                                </span>
                                <span className="block">
                                    {depTime.dateChanged ? (
                                        <>
                                            <span className="mr-1.5">{flightService.formatFlightDate(depTime.effective)}</span>
                                            <span className="line-through opacity-75">{flightService.formatFlightDate(depTime.scheduled)}</span>
                                        </>
                                    ) : (
                                        depTime.effective ? flightService.formatFlightDate(depTime.effective) : ''
                                    )}
                                </span>
                            </div>
                            <div className="relative z-10 w-14 h-14 max-md:h-12 max-md:w-12 bg-(--accent-green-light) flex justify-center items-center rounded-full shrink-0">
                                <img src={plane2d} alt="" className="w-8 h-8"/>
                            </div>
                            <div className="text-sm min-w-0 flex-1 text-right">
                                <span className="block">
                                    {flightDetails.arrival?.airport?.municipalityName || '—'} {flightDetails.arrival?.airport?.iata ? `(${flightDetails.arrival.airport.iata})` : ''}
                                </span>
                                <span className="block text-5xl font-bold">
                                    {arrTime.timeChanged ? (
                                        <>
                                            <span className="mr-2.5">{flightService.formatFlightTime(arrTime.effective)}</span>
                                            <span className="text-lg line-through">{flightService.formatFlightTime(arrTime.scheduled)}</span>
                                        </>
                                    ) : (
                                        arrTime.scheduled ? flightService.formatFlightTime(arrTime.scheduled) : '—'
                                    )}
                                </span>
                                <span className="block">
                                    {arrTime.dateChanged ? (
                                        <>
                                            <span className="mr-1.5">{flightService.formatFlightDate(arrTime.effective)}</span>
                                            <span className="line-through opacity-75">{flightService.formatFlightDate(arrTime.scheduled)}</span>
                                        </>
                                    ) : (
                                        arrTime.effective ? flightService.formatFlightDate(arrTime.effective) : ''
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Flight/airport mismatch soft-validation — the flight card above stays
                    visible; the recommended pickup is withheld until "Proceed anyway". */}
                <FlightMismatchWarning flightMismatch={flightMismatch} message={flightMismatchMsg} acknowledged={acknowledged} proceeding={proceeding} onProceed={handleProceedAnyway}/>

                {/* Cancelled flight — same acknowledge gate. When a mismatch is showing too,
                    that warning owns the single Proceed button, so this one hides its own. */}
                <FlightCancelledWarning flightCancelled={flightCancelled} acknowledged={acknowledged} proceeding={proceeding} onProceed={handleProceedAnyway} showProceed={!flightMismatch}/>

                {/* Provider unreachable — "Fail to get the details from server" + Proceed
                    anyway. No flight card to show (we got no data); acknowledging opens the
                    submit gate so the user can keep the flight already on the booking. */}
                <FlightUnavailableWarning show={providerUnavailable} acknowledged={acknowledged} proceeding={proceeding} onProceed={handleProceedUnavailable}/>

                {/* Recommended pickup — new to/from airport flow */}
                {calc && !hideTimeSuggestion && (!flightBlocked || acknowledged) && (
                    <div className="hopn-smart-time-result">
                        {isMobile && isFromAirport && (
                            <h3 className="hopn-pickup-options-title outer">How would you like to set your pickup time?</h3>
                        )}

                        <TimeErrorWarning timeError={timeError}/>

                        {isFromAirport ? (
                            <div className="hopn-pickup-options from-airport">
                                <div className="hopn-pickup-options-grid">
                                    <div className="hopn-pickup-options-grid-col">
                                        <div
                                            className={`hopn-pickup-option recommended ${isRecommendedActive ? 'selected' : 'section-disabled'}`}
                                            onClick={!isRecommendedActive ? () => setSelectedSection('recommended') : undefined}
                                        >
                                            {!isMobile && (
                                                <>
                                                    <h3 className="hopn-pickup-options-title">How would you like to set your pickup time?</h3>
                                                    <p className="hopn-pickup-options-subtitle">Choose the option that works best for you</p>
                                                </>
                                            )}
                                            <div className="hopn-pickup-option-top">
                                                <span className="hopn-pickup-option-tag">RECOMMENDED</span>
                                                <button
                                                    type="button"
                                                    className="hopn-pickup-option-best section-radio"
                                                    onClick={() => setSelectedSection('recommended')}
                                                    aria-pressed={isRecommendedActive}
                                                    aria-label="Select recommended pickup"
                                                >
                                                    <h6>Best choice</h6>
                                                    <img src={selectedIcon} alt=""/>
                                                </button>
                                            </div>
                                            <div className="recommendation-tabs">
                                                <div className="recommendation-list">
                                                    <span><img src={boldCar} alt=""/></span>
                                                    <div className="recommendation-content">
                                                        <div className="recommendation-content-list">
                                                            <h3>Recommended pickup <span className="mb-3">{formatTime(calc.recommendedTime)}</span></h3>
                                                        </div>
                                                        <div className="recommendation-content-data">
                                                            <h6>No waiting charges</h6>
                                                        </div>
                                                        <div className="recommendation-content-data">
                                                            {/* The airport's own waiting_times items (/config/airports). */}
                                                            <ul className="hopn-pickup-breakdown-list">
                                                                {waitingItems.map((item, i) => (
                                                                    <li key={i}>
                                                                        <span className="hopn-breakdown-icon"><img src={waitingItemImage(item.name)} alt=""/></span>
                                                                        <span className="hopn-breakdown-label">{item.name}</span>
                                                                        <span className="hopn-breakdown-value">~{formatBufferApprox(item.minutes)}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                            <p className="hopn-pickup-total">
                                                                <span className="hopn-pickup-total-label"><strong>Total</strong> estimated time at airport:</span>
                                                                <span className="hopn-pickup-total-value">~{formatBufferApprox(airportBuffer)} before departure <img src={infoIcon} alt=""/></span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="recommendation-list">
                                                    <span><img src={planeTakeoffBold} alt=""/></span>
                                                    <div className="recommendation-content">
                                                        <div className="recommendation-content-list">
                                                            <h3>We track your flight in real time <div className="local">Your driver adjusts automatically if your flight is delayed or arrives early. No action needed</div></h3>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={`hopn-pickup-options-grid-col gray ${isCustomActive ? 'selected' : 'section-disabled'}`}
                                        onClick={!isCustomActive ? () => setSelectedSection('custom') : undefined}
                                    >
                                        <div className="hopn-pickup-option-top hopn-pickup-option-top--custom">
                                            <button
                                                type="button"
                                                className="hopn-pickup-option-best section-radio"
                                                onClick={() => setSelectedSection('custom')}
                                                aria-pressed={isCustomActive}
                                                aria-label="Select custom pickup time"
                                            >
                                                <img src={selectedIcon} alt=""/>
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="hopn-pickup-info-row clickable"
                                            disabled={!isCustomActive}
                                            onClick={() => onClose?.()}
                                        >
                                            <div className="hopn-pickup-info-icon"><img src={clockIconBold} alt="Clock"/></div>
                                            <div className="hopn-pickup-info-text">
                                                <h5>Custom pickup time</h5>
                                                <p>Choose your own time</p>
                                            </div>
                                            <span className="hopn-pickup-info-chevron"><img src={arrRight} alt=""/></span>
                                        </button>

                                        {!isMobile && (
                                            <>
                                                <div className="hopn-pickup-info-row">
                                                    <div className="hopn-pickup-info-icon"><img src={stopWatchBold} alt="Stopwatch"/></div>
                                                    <div className="hopn-pickup-info-text">
                                                        <h5>15 minutes free waiting</h5>
                                                        <p>Your driver will wait free for 15 minutes after your selected pickup time</p>
                                                    </div>
                                                </div>
                                                <div className="hopn-pickup-info-row">
                                                    <div className="hopn-pickup-info-icon"><img src={cardBold} alt="Card"/></div>
                                                    <div className="hopn-pickup-info-text">
                                                        <h5>After 15 minutes</h5>
                                                        <p>$25 will be charged per hour, billed to your saved payment method</p>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <ToAirportRecommendedCard calc={calc} expanded={toAirportExpanded} onToggle={() => setToAirportExpanded(v => !v)} waitingItems={waitingItems} airportBuffer={airportBuffer} arriveEarlyMinutes={arriveEarlyMinutes}/>
                        )}

                        <ModalFooter>
                            <button
                                type="button"
                                className="hopn-add-passenger-button duble-title"
                                disabled={!!timeError}
                                onClick={confirmPickupTime}
                            >
                                <div className="multiple-text">
                                    <span>Confirm pickup time</span>
                                    <span>{confirmDateTimeStr}</span>
                                </div>
                            </button>

                            {(!isFromAirport || isCustomActive) && (
                                <button type="button" className="hopn-choose-own-time-link" onClick={() => onClose?.()}>
                                    I'll choose my own time <img src={arrRight} alt=""/>
                                </button>
                            )}
                        </ModalFooter>
                    </div>
                )}
            </div>
        </>
    )
}
